
import Vue from 'vue'

interface CardItem {
  title: string
  text: string
}

interface VisualItem {
  title: string
  text: string
  image: string
}

interface GalleryItem {
  src: string
  alt: string
  caption: string
}

interface RoadmapItem {
  phase: string
  title: string
  text: string
}

export default Vue.extend({
  name: 'LordOfApplesPage',

  data () {
    return {
      highlights: [
        {
          title: 'Math-First Gameplay',
          text: 'Core progression is powered by arithmetic decisions integrated directly into exploration and battle flow.'
        },
        {
          title: 'Hero Progression Paths',
          text: 'Each hero class supports a distinct style, with stat growth and ability scaling tied to learning milestones.'
        },
        {
          title: 'Boss Logic Challenges',
          text: 'Boss encounters combine combat timing with equation and sequencing tasks for deeper player engagement.'
        },
        {
          title: 'Rewarded Learning Loop',
          text: 'Players collect resources, unlock upgrades, and move forward through structured, skill-based progression.'
        }
      ] as CardItem[],
      resources: [
        {
          title: 'Golden Apple Coin',
          text: 'Primary premium currency used for advanced upgrades and gated progression actions.',
          image: '/games/lord-of-apples/assets/resource-golden-apple-coin.webp'
        },
        {
          title: 'Honey Energy Drop',
          text: 'Consumable energy resource for mission stamina and continuous gameplay flow.',
          image: '/games/lord-of-apples/assets/resource-honey-energy-drop.webp'
        },
        {
          title: 'Leaf Token',
          text: 'Seasonal token used for class unlocks, event progression, and strategic inventory actions.',
          image: '/games/lord-of-apples/assets/resource-leaf-token.webp'
        },
        {
          title: 'Star Seed',
          text: 'Progression catalyst item used to activate key unlock checkpoints and challenge tiers.',
          image: '/games/lord-of-apples/assets/resource-star-seed.webp'
        },
        {
          title: 'Number Crystal',
          text: 'Core arithmetic artefact linked to puzzle intensity scaling and logic-driven reward systems.',
          image: '/games/lord-of-apples/assets/resource-number-crystal.webp'
        }
      ] as VisualItem[],
      heroes: [
        {
          title: 'Geo Swift, the Orchard Ranger',
          text: 'Agile ranged class focused on speed, precision, and quick-response problem solving.',
          image: '/games/lord-of-apples/assets/hero-geo-swift.webp'
        },
        {
          title: 'Lady Sumella, the Math Mage',
          text: 'Support/control specialist using equation buffs, multiplier logic, and tactical resets.',
          image: '/games/lord-of-apples/assets/hero-lady-sumella.webp'
        },
        {
          title: 'The Apple Count Knight',
          text: 'Frontline tank class built around stable defence, sequence mastery, and objective control.',
          image: '/games/lord-of-apples/assets/hero-apple-count-knight.webp'
        }
      ] as VisualItem[],
      bosses: [
        {
          title: 'Baron Rotten Core',
          text: 'Corruption boss introducing decay mechanics and subtraction-driven pressure phases.',
          image: '/games/lord-of-apples/assets/boss-baron-rotten-core.webp'
        },
        {
          title: 'Queen Wormatrix',
          text: 'Adaptive queen encounter with layered multiplication patterns and shifting puzzle states.',
          image: '/games/lord-of-apples/assets/boss-queen-wormatrix.webp'
        },
        {
          title: 'The Clockwork Orchard Titan',
          text: 'High-tier guardian featuring clocked cycles, weighted equations, and endurance mechanics.',
          image: '/games/lord-of-apples/assets/boss-clockwork-orchard-titan.webp'
        }
      ] as VisualItem[],
      gallery: [
        {
          src: '/games/lord-of-apples/assets/logo-lord-of-apples.webp',
          alt: 'Lord of Apples logo concept',
          caption: 'Primary branding mark for the current concept direction.'
        },
        {
          src: '/games/lord-of-apples/assets/main-game-background.webp',
          alt: 'Apple Kingdom learning world concept scene',
          caption: 'World environment concept used for level and narrative framing.'
        },
        {
          src: '/games/lord-of-apples/assets/dashboard-background.webp',
          alt: 'Premium Orchard Command Hall concept',
          caption: 'Command interface environment for profile, upgrades, and progression control.'
        },
        {
          src: '/games/lord-of-apples/assets/hero-geo-swift.webp',
          alt: 'Geo Swift hero concept',
          caption: 'Ranger hero concept for mobility and precision-focused sessions.'
        },
        {
          src: '/games/lord-of-apples/assets/hero-lady-sumella.webp',
          alt: 'Lady Sumella hero concept',
          caption: 'Math mage hero concept designed for support and arithmetic modifiers.'
        },
        {
          src: '/games/lord-of-apples/assets/hero-apple-count-knight.webp',
          alt: 'Apple Count Knight hero concept',
          caption: 'Knight hero concept designed for defence and sequence stability.'
        },
        {
          src: '/games/lord-of-apples/assets/boss-baron-rotten-core.webp',
          alt: 'Baron Rotten Core boss concept',
          caption: 'Boss visual concept for early corruption-focused encounters.'
        },
        {
          src: '/games/lord-of-apples/assets/boss-queen-wormatrix.webp',
          alt: 'Queen Wormatrix boss concept',
          caption: 'Boss concept with adaptive patterns and challenge modifiers.'
        },
        {
          src: '/games/lord-of-apples/assets/boss-clockwork-orchard-titan.webp',
          alt: 'Clockwork Orchard Titan boss concept',
          caption: 'High-tier boss concept for advanced arithmetic-combat phases.'
        }
      ] as GalleryItem[],
      roadmap: [
        {
          phase: 'Phase 01',
          title: 'Core Loop Foundation',
          text: 'Arithmetic interaction, movement flow, and base progression systems are being finalised.'
        },
        {
          phase: 'Phase 02',
          title: 'Hero and Economy Expansion',
          text: 'Class balancing, reward logic, and challenge-pacing systems are being expanded and tuned.'
        },
        {
          phase: 'Phase 03',
          title: 'Boss Layer and Launch Preparation',
          text: 'Endgame encounters, polish, and release readiness workflows are planned for delivery.'
        }
      ] as RoadmapItem[],
      activeItem: null as GalleryItem | null
    }
  },

  mounted () {
    window.addEventListener('keydown', this.handleEscape)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.handleEscape)
  },

  methods: {
    openModal (item: GalleryItem) {
      this.activeItem = item
    },
    closeModal () {
      this.activeItem = null
    },
    handleEscape (event: KeyboardEvent) {
      if (event.key === 'Escape' && this.activeItem) {
        this.closeModal()
      }
    }
  }
})
